import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, type UseFormReturn, useWatch } from "react-hook-form";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import type { RootState } from "~/app/store";
import { ProductService } from "~/features/addToCart/api/ProductService";
import { selectProperAmountInCart } from "~/features/addToCart/redux/cartAmounts/cartAmountSelectors";
import { getInitialPriceReference } from "~/features/addToCart/redux/addToCart/getInitialPriceReference";
import { requestFullProductData } from "~/domains/product/redux/actions";
import { selectProductDescriptor } from "~/domains/product/redux/selectors";
import { useEffectivePackaging } from "~/domains/product/hooks/useEffectivePackaging";
import { buildSimpleEffectivePackagingEntry } from "~/domains/product/utils/buildEffectivePackaging";
import type { EffectivePackagingEntry } from "~/domains/product/types/EffectivePackaging";
import { getNetPrice, getPackagingInformation } from "~/domains/product/utils/productDescriptorUtils";
import { type HousingProduct, isKITProduct, type PackagingInformationEntry } from "~/domains/product/types/ProductData";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import type { NetPriceData } from "~/domains/pricing/types/PriceData";
import { INVALID_PACKAGE_CODE } from "~/utils/constants";
import { isSelectablePack } from "~/domains/product/utils/isSelectablePack";
import { getDefaultQuantity } from "~/features/productDetails/components/ProductSummary/common/usePdpSummaryLogic";
import { isMinOrderQuantityRestrictionEnabled } from "~/features/cart/cart/utils/cartUtils";

export interface AddToCartFormDraft {
  quantity: number;
  priceReference: string;
  selectedPackCode: string;
  comment?: string;
  salePriceEnabled: boolean;
  optionalParts: Record<string, OptionalPartDraft>;
}

export interface OptionalPartDraft {
  productId: string;
  selected: boolean;
  quantity: number;
  selectedPackCode: string;
}

export type OptionalPartMetaMap = Record<
  string,
  {
    isQtyUserModified: boolean;
  }
>;

export interface MainProductDerived {
  selectedPack: PackagingInformationEntry | null;
  selectedPackaging: EffectivePackagingEntry | null;
  netPrice: NetPriceData | null;
  totalQuantity: number;
  descriptor: ProductDescriptor;
}

export interface OptionalPartDerived {
  productId: string;
  selectedPack: PackagingInformationEntry | null;
  selectedPackaging: EffectivePackagingEntry | null;
  netPrice: NetPriceData | null;
  totalQuantity: number;
  descriptor: ProductDescriptor;
}

export interface OptionalPartViewModel {
  housingProduct: HousingProduct;
  draft: OptionalPartDraft;
  derived: OptionalPartDerived;
  meta: {
    isQtyUserModified: boolean;
  };
}

export interface OptionalPartSubmitData {
  formValues: OptionalPartDraft;
  derived: OptionalPartDerived;
  meta: {
    isQtyUserModified: boolean;
  };
}

export interface AddToCartSubmitData {
  formValues: AddToCartFormDraft;
  derived: MainProductDerived;
  optionalParts: Record<string, OptionalPartSubmitData>;
}

export type PackCodeProvider = (product: ProductDescriptor, quantity: number) => string;

export const makeInitialOptionalPartsDraft = (
  parts: HousingProduct[],
  kitQty = 1,
): Record<string, OptionalPartDraft> => {
  return parts.reduce<Record<string, OptionalPartDraft>>((acc, item) => {
    acc[item.id] = {
      productId: item.id,
      selected: true,
      quantity: item.quantity * kitQty,
      selectedPackCode: INVALID_PACKAGE_CODE,
    };
    return acc;
  }, {});
};

export interface AddToCartHookProps {
  productCode: string;
  editItemId?: string | null;
  initialData?: Partial<AddToCartFormDraft>;
  initialPackCodeProvider?: PackCodeProvider | false;
  onInitialPackCodeSelection?: (packCode: string) => void;
  editMode?: boolean;
  fetchProductData?: boolean;
  fetchAvailability?: boolean;
  fetchPrice?: boolean;
  descriptorTransform?: (descriptor: ProductDescriptor) => ProductDescriptor;
}

export type AddToCartFormSetValue = UseFormReturn<AddToCartFormDraft>["setValue"];

export type AddToCartFormApi = Omit<UseFormReturn<AddToCartFormDraft>, "setValue"> & {
  setValue: AddToCartFormSetValue;
};

export interface UseAddToCartFormReturn {
  form: AddToCartFormApi;
  productDescriptor: ProductDescriptor;
  main: MainProductDerived;
  initialPackCodeResolved: boolean;
  optionalParts: {
    byId: Record<string, OptionalPartViewModel>;
    ids: string[];
  };
  prepareSubmitData: () => AddToCartSubmitData;
}

export const useAddToCartForm = ({
  productCode,
  initialData: rawInitialData,
  initialPackCodeProvider,
  onInitialPackCodeSelection,
  editMode = false,
  fetchAvailability = true,
  fetchPrice = true,
  fetchProductData = true,
  descriptorTransform,
}: AddToCartHookProps): UseAddToCartFormReturn => {
  const [initialDraft] = useState(() => createInitialFormDraft(rawInitialData, productCode));
  const [initialPackCodeResolved, setInitialPackCodeResolved] = useState(
    () => editMode || initialPackCodeProvider === false || initialDraft.selectedPackCode !== INVALID_PACKAGE_CODE,
  );
  const form = useForm<AddToCartFormDraft>({ defaultValues: initialDraft });
  const dispatch = useDispatch();

  const quantity = useWatch({ control: form.control, name: "quantity" }) ?? initialDraft.quantity;
  const selectedPackCode =
    useWatch({ control: form.control, name: "selectedPackCode" }) ?? initialDraft.selectedPackCode;
  const priceReference = useWatch({ control: form.control, name: "priceReference" }) ?? initialDraft.priceReference;
  const optionalPartDrafts = useWatch({ control: form.control, name: "optionalParts" }) ?? initialDraft.optionalParts;

  const rawProductDescriptor = ProductService.useProductDescriptor({
    productCode,
    priceReference,
    options: {
      fetch: true,
      fetchAvailability,
      fetchPrice,
      forceFetchBasicData: fetchProductData,
    },
  });

  const productDescriptor = useMemo(
    () => (descriptorTransform ? descriptorTransform(rawProductDescriptor) : rawProductDescriptor),
    [descriptorTransform, rawProductDescriptor],
  );
  const effectivePackaging = useEffectivePackaging({
    descriptor: productDescriptor,
    priceReference,
  });

  const housingProducts = useMemo(() => getHousingProducts(productDescriptor), [productDescriptor]);
  const optionalPartIds = useMemo(() => Object.keys(optionalPartDrafts ?? {}), [optionalPartDrafts]);
  const optionalPartMetaRef = useRef<OptionalPartMetaMap>(createInitialOptionalPartMeta(initialDraft.optionalParts));

  useEffect(() => {
    housingProducts.forEach((housingProduct) => {
      dispatch(
        requestFullProductData(housingProduct.id, priceReference, {
          fetchAvailability,
          fetchPrice,
        }),
      );
    });
  }, [dispatch, housingProducts, priceReference, fetchAvailability, fetchPrice]);

  const optionalPartDescriptors = useSelector((state: RootState) => {
    return optionalPartIds.reduce<Record<string, ProductDescriptor>>((acc, optionalPartId) => {
      acc[optionalPartId] = selectProductDescriptor(state, { productCode: optionalPartId, priceReference });
      return acc;
    }, {});
  }, shallowEqual);

  useEffect(() => {
    optionalPartIds.forEach((optionalPartId) => {
      const draft = form.getValues(`optionalParts.${optionalPartId}`);
      if (!draft || draft.selectedPackCode !== INVALID_PACKAGE_CODE) {
        return;
      }
      const descriptor = optionalPartDescriptors[optionalPartId];
      const nextPackCode = getDefaultPackCode(descriptor, draft.quantity);
      if (nextPackCode !== INVALID_PACKAGE_CODE) {
        form.setValue(`optionalParts.${optionalPartId}.selectedPackCode`, nextPackCode, {
          shouldDirty: false,
          shouldTouch: false,
        });
      }
    });
  }, [form, optionalPartDescriptors, optionalPartIds]);

  const mainCartQty = useSelector(
    selectProperAmountInCart({
      productCode,
      packCode: selectedPackCode,
      priceReference,
    }),
  );

  const optionalPartCartQtyById = useSelector((state: RootState) => {
    return Object.entries(optionalPartDrafts).reduce<Record<string, number>>((acc, [optionalPartId, draft]) => {
      acc[optionalPartId] = selectProperAmountInCart({
        productCode: optionalPartId,
        packCode: draft.selectedPackCode,
        priceReference,
      })(state);
      return acc;
    }, {});
  }, shallowEqual);

  const main = useMemo<MainProductDerived>(() => {
    const totalQuantity = quantity - (editMode ? rawInitialData?.quantity ?? 0 : 0) + mainCartQty;
    const selectedPack = getPackagingInformation(productDescriptor, selectedPackCode);
    const selectedPackaging =
      effectivePackaging.byPackCode[selectedPackCode] ??
      (selectedPack ? buildSimpleEffectivePackagingEntry(productDescriptor, selectedPack) : null);
    const netPrice =
      fetchPrice && selectedPack
        ? getNetPrice({
            totalQuantity,
            product: productDescriptor,
            packCode: selectedPack.packCode,
          })
        : null;

    return {
      selectedPack,
      selectedPackaging,
      netPrice,
      totalQuantity,
      descriptor: productDescriptor,
    };
  }, [
    editMode,
    effectivePackaging.byPackCode,
    fetchPrice,
    rawInitialData?.quantity,
    mainCartQty,
    productDescriptor,
    quantity,
    selectedPackCode,
  ]);

  const optionalParts = useMemo(() => {
    const byId = housingProducts.reduce<Record<string, OptionalPartViewModel>>((acc, housingProduct) => {
      const draft = optionalPartDrafts[housingProduct.id] ?? createOptionalPartDraft(housingProduct, quantity);
      const descriptor = optionalPartDescriptors[housingProduct.id];
      const selectedPack = getPackagingInformation(descriptor, draft.selectedPackCode);
      const selectedPackaging =
        descriptor && selectedPack ? buildSimpleEffectivePackagingEntry(descriptor, selectedPack) : null;
      const cartQty = optionalPartCartQtyById[housingProduct.id] ?? 0;
      const totalQuantity = draft.quantity + cartQty;
      const netPrice =
        fetchPrice && descriptor && selectedPack
          ? getNetPrice({
              totalQuantity,
              product: descriptor,
              packCode: selectedPack.packCode,
            })
          : null;

      acc[housingProduct.id] = {
        housingProduct,
        draft,
        derived: {
          productId: housingProduct.id,
          selectedPack,
          selectedPackaging,
          netPrice,
          totalQuantity,
          descriptor,
        },
        meta: {
          isQtyUserModified: optionalPartMetaRef.current[housingProduct.id]?.isQtyUserModified ?? false,
        },
      };
      return acc;
    }, {});

    return {
      byId,
      ids: housingProducts.map((item) => item.id),
    };
  }, [fetchPrice, housingProducts, optionalPartCartQtyById, optionalPartDescriptors, optionalPartDrafts, quantity]);

  const baseSetValue = form.setValue;

  const setValue = useCallback<AddToCartFormSetValue>(
    ((name, value, options) => {
      const fieldName = String(name);

      if (fieldName === "quantity") {
        baseSetValue(name, value, options);
        const nextDrafts = syncOptionalPartDraftQuantities({
          nextParentQuantity: value as number,
          currentDrafts: form.getValues("optionalParts"),
          metaById: optionalPartMetaRef.current,
          housingProducts,
        });
        baseSetValue("optionalParts", nextDrafts, { shouldDirty: true, shouldTouch: false });
        return;
      }

      if (fieldName === "selectedPackCode") {
        baseSetValue(name, value, options);
        if (!editMode && !form.formState.touchedFields.quantity) {
          const nextQuantity = deriveDefaultQuantityFromPack(
            productDescriptor,
            String(value),
            effectivePackaging.byPackCode[String(value)] ?? null,
          );
          if (nextQuantity != null) {
            setValue("quantity", nextQuantity, { shouldDirty: true, shouldTouch: false });
          }
        }
        return;
      }

      const quantityMatch = fieldName.match(/^optionalParts\.([^.]+)\.quantity$/);
      if (quantityMatch) {
        optionalPartMetaRef.current[quantityMatch[1]] = { isQtyUserModified: true };
        baseSetValue(name, value, options);
        return;
      }

      baseSetValue(name, value, options);
    }) as AddToCartFormSetValue,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setValue is intentionally self-referential for the quantity branch
    [baseSetValue, editMode, effectivePackaging.byPackCode, form, housingProducts, productDescriptor],
  );

  const proxiedForm: AddToCartFormApi = { ...form, setValue };

  useEffect(() => {
    if (editMode) {
      setInitialPackCodeResolved(true);
      return;
    }
    if (initialPackCodeProvider === false) {
      setInitialPackCodeResolved(true);
      return;
    }
    if (form.getValues("selectedPackCode") !== INVALID_PACKAGE_CODE) {
      setInitialPackCodeResolved(true);
      return;
    }
    if (!productDescriptor.isLoaded) {
      return;
    }

    const nextPackCode =
      initialPackCodeProvider?.(productDescriptor, form.getValues("quantity")) ??
      getDefaultPackCode(productDescriptor, form.getValues("quantity"));

    if (nextPackCode === INVALID_PACKAGE_CODE) {
      setInitialPackCodeResolved(true);
      return;
    }

    setValue("selectedPackCode", nextPackCode, { shouldDirty: false, shouldTouch: false });
    onInitialPackCodeSelection?.(nextPackCode);
    setInitialPackCodeResolved(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally triggered only on isLoaded transition; productDescriptor read via getValues/closure is stable
  }, [editMode, productDescriptor.isLoaded, initialPackCodeProvider, onInitialPackCodeSelection, form, setValue]);

  const prepareSubmitData = useCallback((): AddToCartSubmitData => {
    const draft = form.getValues();

    return {
      formValues: draft,
      derived: main,
      optionalParts: Object.entries(optionalParts.byId).reduce<Record<string, OptionalPartSubmitData>>(
        (acc, [productId, part]) => {
          acc[productId] = {
            formValues: part.draft,
            derived: part.derived,
            meta: part.meta,
          };
          return acc;
        },
        {},
      ),
    };
  }, [form, main, optionalParts]);

  return {
    form: proxiedForm,
    productDescriptor,
    main,
    initialPackCodeResolved,
    optionalParts,
    prepareSubmitData,
  };
};

function createInitialFormDraft(
  initialData: Partial<AddToCartFormDraft> = {},
  productCode: string,
): AddToCartFormDraft {
  const product = ProductService.getBaseProductData(productCode);
  const quantity = initialData.quantity ?? 1;
  const housingProducts = product && isKITProduct(product) ? product.optionalParts : [];

  return {
    quantity,
    priceReference: initialData.priceReference ?? getInitialPriceReference(),
    selectedPackCode: initialData.selectedPackCode ?? INVALID_PACKAGE_CODE,
    comment: initialData.comment ?? "",
    salePriceEnabled: initialData.salePriceEnabled ?? true,
    optionalParts: mergeOptionalPartDrafts(initialData.optionalParts ?? {}, housingProducts, quantity),
  };
}

function createOptionalPartDraft(housingProduct: HousingProduct, parentQuantity: number): OptionalPartDraft {
  return {
    productId: housingProduct.id,
    selected: true,
    quantity: housingProduct.quantity * parentQuantity,
    selectedPackCode: INVALID_PACKAGE_CODE,
  };
}

function mergeOptionalPartDrafts(
  currentDrafts: Record<string, OptionalPartDraft>,
  housingProducts: HousingProduct[],
  parentQuantity: number,
): Record<string, OptionalPartDraft> {
  return housingProducts.reduce<Record<string, OptionalPartDraft>>((acc, housingProduct) => {
    const currentDraft = currentDrafts[housingProduct.id];
    acc[housingProduct.id] = currentDraft ?? createOptionalPartDraft(housingProduct, parentQuantity);
    return acc;
  }, {});
}

function createInitialOptionalPartMeta(optionalParts: Record<string, OptionalPartDraft>): OptionalPartMetaMap {
  return Object.keys(optionalParts).reduce<OptionalPartMetaMap>((acc, productId) => {
    acc[productId] = { isQtyUserModified: false };
    return acc;
  }, {});
}

function syncOptionalPartDraftQuantities({
  nextParentQuantity,
  currentDrafts,
  metaById,
  housingProducts,
}: {
  nextParentQuantity: number;
  currentDrafts: Record<string, OptionalPartDraft>;
  metaById: OptionalPartMetaMap;
  housingProducts: HousingProduct[];
}) {
  return housingProducts.reduce<Record<string, OptionalPartDraft>>((acc, housingProduct) => {
    const currentDraft =
      currentDrafts[housingProduct.id] ?? createOptionalPartDraft(housingProduct, nextParentQuantity);
    const isQtyUserModified = metaById[housingProduct.id]?.isQtyUserModified ?? false;

    acc[housingProduct.id] = isQtyUserModified
      ? currentDraft
      : { ...currentDraft, quantity: housingProduct.quantity * nextParentQuantity };

    return acc;
  }, {});
}

function getHousingProducts(productDescriptor: ProductDescriptor): HousingProduct[] {
  return isKITProduct(productDescriptor) ? productDescriptor.base.optionalParts : [];
}

function deriveDefaultQuantityFromPack(
  productDescriptor: ProductDescriptor,
  packCode: string,
  selectedPackaging: EffectivePackagingEntry | null,
): number | null {
  const selectedPack = getPackagingInformation(productDescriptor, packCode);
  const packSize = selectedPackaging?.packSize ?? selectedPack?.packSize;

  if (!selectedPack || !packSize) {
    return null;
  }

  if (isMinOrderQuantityRestrictionEnabled()) {
    return getDefaultQuantity(packSize, selectedPack.minOrderQuantity);
  }

  return packSize;
}

function getDefaultPackCode(productDescriptor: ProductDescriptor, quantity: number): string {
  if (!productDescriptor?.availability?.packagingInformation?.length) {
    return INVALID_PACKAGE_CODE;
  }

  const selectablePacks = productDescriptor.availability.packagingInformation.filter((pack) =>
    isSelectablePack(productDescriptor, pack),
  );

  if (selectablePacks.length === 0) {
    return INVALID_PACKAGE_CODE;
  }

  const exactPack = selectablePacks.find((pack) => pack.packSize === quantity);
  if (exactPack) {
    return exactPack.packCode;
  }

  return selectablePacks[0]?.packCode ?? INVALID_PACKAGE_CODE;
}
